import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { PerfTableColumn, SplitBarTone, ViewToggleOption } from "../data-visualization";
import { DashboardTabAbstractComponent } from "../dashboard-tab-abstract.component";
import { AlertService, DashboardService, ResolveReportService } from "../../_services";
import {
  ReportDimensions,
  ResolveSourceBreakdown,
  ResolveSourceBreakdownRow,
  ResolveSummaryResponse,
} from "../../reports/models/reports";
import {
  ResolveDestinationDimension,
  ResolveDimension,
  ResolveRequestSourceDimension,
  buildResolveBuyAgainStats,
  buildResolveCampaignsOriginated,
  buildResolveExternalLinksData,
  buildResolveExternalLinksLegend,
  buildResolveExternalLinksShare,
  buildResolveReceiptCard,
  buildResolveReceiptMatchedPercentage,
  buildResolveReceiptSplit,
  buildResolveRefundStats,
  buildResolveRequestOutcomes,
  buildResolveRequestSource,
  buildResolveSourceBreakdown,
  buildResolveTopIntents,
  buildResolveTopMoments,
  buildResolveTopProductsPurchased,
  buildResolveViewOrderStats,
  resolveAiInsights,
  resolveBuyAgainStats,
  resolveCampaignsNameColumn,
  resolveCampaignsOriginated,
  resolveCampaignsValueColumns,
  resolveDestinationDimensionOptions,
  resolveExternalLinksData,
  resolveExternalLinksLegend,
  resolveExternalLinksShare,
  resolveHints,
  resolveSectionIcons,
  resolveProductsNameColumn,
  resolveProductsValueColumns,
  resolveReceiptCard,
  resolveReceiptMatchedPercentage,
  resolveReceiptSplit,
  resolveRefundStats,
  resolveRequestOutcomes,
  resolveRequestOutcomesNameColumn,
  resolveRequestOutcomesValueColumns,
  resolveRequestSource,
  resolveRequestSourceDimensionOptions,
  resolveRequestSourceNameColumn,
  resolveRequestSourceValueColumns,
  resolveSourceBreakdown,
  resolveSourceBreakdownNameColumn,
  resolveSourceBreakdownValueColumns,
  resolveTopIntents,
  resolveTopIntentsNameColumn,
  resolveTopIntentsValueColumns,
  resolveTopMoments,
  resolveTopMomentsNameColumn,
  resolveTopMomentsValueColumns,
  resolveTopProductsPurchased,
  resolveViewOrderStats,
} from "./resolve.data";

@Component({
  selector: "app-resolve-dashboard",
  templateUrl: "./resolve.component.html",
  styleUrls: ["./../dashboard-section.scss", "./../dashboard-section-v2.scss"],
})
export class DashboardResolveComponent extends DashboardTabAbstractComponent implements OnInit, OnDestroy {
  @Input() cardsLoading: boolean = false;
  @Output() reportNavigate = new EventEmitter<string>();

  hints = resolveHints;
  icons = resolveSectionIcons;
  reportDimensions = ReportDimensions;

  summaryLoading: boolean = false;
  summaryHasData: boolean = false;

  splitBarTone = SplitBarTone;
  receiptCard = resolveReceiptCard;
  receiptMatchedPercentage = resolveReceiptMatchedPercentage;
  receiptSplit = resolveReceiptSplit;
  externalLinksData = resolveExternalLinksData;
  externalLinksLegend = resolveExternalLinksLegend;
  externalLinksShare = resolveExternalLinksShare;
  topMoments = resolveTopMoments;
  topMomentsNameColumn = resolveTopMomentsNameColumn;
  topMomentsValueColumns = resolveTopMomentsValueColumns;

  viewOrderStats = resolveViewOrderStats;
  sourceBreakdown = resolveSourceBreakdown;
  sourceBreakdownNameColumn = resolveSourceBreakdownNameColumn;
  sourceBreakdownValueColumns = resolveSourceBreakdownValueColumns;
  topIntents = resolveTopIntents;
  topIntentsNameColumn = resolveTopIntentsNameColumn;
  topIntentsValueColumns = resolveTopIntentsValueColumns;

  buyAgainStats = resolveBuyAgainStats;
  campaignsOriginated = resolveCampaignsOriginated;
  campaignsNameColumn = resolveCampaignsNameColumn;
  campaignsValueColumns = resolveCampaignsValueColumns;
  topProductsPurchased = resolveTopProductsPurchased;
  productsNameColumn = resolveProductsNameColumn;
  productsValueColumns = resolveProductsValueColumns;

  refundStats = resolveRefundStats;
  requestSource = resolveRequestSource;
  requestSourceNameColumn = resolveRequestSourceNameColumn;
  requestSourceValueColumns = resolveRequestSourceValueColumns;
  requestOutcomes = resolveRequestOutcomes;
  requestOutcomesNameColumn = resolveRequestOutcomesNameColumn;
  requestOutcomesValueColumns = resolveRequestOutcomesValueColumns;

  aiInsights = resolveAiInsights;

  destinationDimensionOptions = resolveDestinationDimensionOptions;
  requestSourceDimensionOptions = resolveRequestSourceDimensionOptions;
  sourceBreakdownDimension: ResolveDestinationDimension = ResolveDestinationDimension.Product;
  requestSourceDimension: ResolveRequestSourceDimension = ResolveRequestSourceDimension.MerchantProfile;

  private lastResponse: ResolveSummaryResponse = null;
  private _destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private dashboardService: DashboardService,
    private resolveReportService: ResolveReportService,
    private alertService: AlertService
  ) {
    super();
  }

  ngOnInit() {
    this.dashboardService.filters$
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.loadSummary();
      });
  }

  ngOnDestroy() {
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  navigateToReport(dimension: string) {
    this.reportNavigate.emit(dimension);
  }

  onSourceBreakdownDimensionChange(dimension: ResolveDestinationDimension) {
    this.sourceBreakdownDimension = dimension;
    this.sourceBreakdownNameColumn = this.buildNameColumn(this.destinationDimensionOptions, dimension);
    this.applySourceBreakdown();
  }

  onRequestSourceDimensionChange(dimension: ResolveRequestSourceDimension) {
    this.requestSourceDimension = dimension;
    this.requestSourceNameColumn = this.buildNameColumn(this.requestSourceDimensionOptions, dimension);
  }

  buildNameColumn(options: ViewToggleOption[], value: ResolveDimension): PerfTableColumn {
    let label = "";
    const selected = options.find((option: ViewToggleOption) => option.value === value);

    if (selected) {
      label = selected.label;
    }

    return { label, field: "name" };
  }

  private loadSummary() {
    this.summaryLoading = true;
    this.topMoments = { ...this.topMoments, loading: true };
    this.sourceBreakdown = { ...this.sourceBreakdown, loading: true };
    this.topIntents = { ...this.topIntents, loading: true };
    this.externalLinksData = { ...this.externalLinksData, loading: true };
    this.campaignsOriginated = { ...this.campaignsOriginated, loading: true };
    this.topProductsPurchased = { ...this.topProductsPurchased, loading: true };
    this.requestSource = { ...this.requestSource, loading: true };
    this.requestOutcomes = { ...this.requestOutcomes, loading: true };

    this.resolveReportService
      .getSummary(this.dashboardService.getResolveSummaryBody())
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (result) => {
          const response = result && result[0] ? result[0] : null;
          this.applySummary(response);
          this.summaryLoading = false;
        },
        () => {
          this.applySummary(null);
          this.summaryLoading = false;
          this.alertService.error("An error occurred while loading the Resolve dashboard");
        }
      );
  }

  private applySummary(response: ResolveSummaryResponse) {
    this.lastResponse = response;
    this.resolveReportService.lastSummary = response;

    if (response) {
      this.summaryHasData = false;
      if (response.overview && response.overview.lookups > 0) {
        this.summaryHasData = true;
      }
      this.receiptCard = buildResolveReceiptCard(response);
      this.receiptMatchedPercentage = buildResolveReceiptMatchedPercentage(response);
      this.receiptSplit = buildResolveReceiptSplit(response);
      this.externalLinksData = buildResolveExternalLinksData(response);
      this.externalLinksLegend = buildResolveExternalLinksLegend(response);
      this.externalLinksShare = buildResolveExternalLinksShare(response);
      this.topMoments = {
        data: buildResolveTopMoments(response),
        loading: false,
        noData: response.top_moments.length === 0,
      };
      this.viewOrderStats = buildResolveViewOrderStats(response);
      this.topIntents = {
        data: buildResolveTopIntents(response),
        loading: false,
        noData: response.top_intents.length === 0,
      };
      this.applySourceBreakdown();
      this.applyBuyAgain(response);
      this.applyRequestRefund(response);
    } else {
      this.summaryHasData = false;
      this.topMoments = { data: [], loading: false, noData: true };
      this.sourceBreakdown = { data: [], loading: false, noData: true };
      this.topIntents = { data: [], loading: false, noData: true };
      this.externalLinksData = { ...this.externalLinksData, loading: false, noData: true };
      this.applyBuyAgain(null);
      this.applyRequestRefund(null);
    }
  }

  private applySourceBreakdown() {
    let rows: ResolveSourceBreakdownRow[] = [];

    if (this.lastResponse && this.lastResponse.source_breakdown) {
      const key = this.sourceBreakdownDimension as keyof ResolveSourceBreakdown;
      rows = this.lastResponse.source_breakdown[key] || [];
    }

    this.sourceBreakdown = {
      data: buildResolveSourceBreakdown(rows),
      loading: false,
      noData: rows.length === 0,
    };
  }

  private applyBuyAgain(response: ResolveSummaryResponse) {
    const campaigns = buildResolveCampaignsOriginated(response);
    const products = buildResolveTopProductsPurchased(response);

    this.buyAgainStats = buildResolveBuyAgainStats(response);
    this.campaignsOriginated = { data: campaigns, loading: false, noData: campaigns.length === 0 };
    this.topProductsPurchased = { data: products, loading: false, noData: products.length === 0 };
  }

  private applyRequestRefund(response: ResolveSummaryResponse) {
    const sources = buildResolveRequestSource(response);
    const outcomes = buildResolveRequestOutcomes(response);

    this.refundStats = buildResolveRefundStats(response);
    this.requestSource = { data: sources, loading: false, noData: sources.length === 0 };
    this.requestOutcomes = { data: outcomes, loading: false, noData: outcomes.length === 0 };
  }
}
